import {api} from "@/shared/api";
import {AddisonEAUConfigDialog, BmdConfigDialog, PanelBase, PersonioConfigDialog} from "./panels";
import {AddIntegrationDialogHandler} from "./addIntegrationDialogHandler";
import {ErrorResponse, FieldError, Integration} from "@/shared/timr";
import {IntegrationType} from "@/shared/timrTypes";

export class Integrations {
	private static readonly EVENT_ADD_INTEGRATION = "integrations.add";
	private static readonly EVENT_ADDED_INTEGRATION = "integrations.added";
	private static readonly EVENT_EDITED_INTEGRATION = "integrations.edited";
	private static readonly EVENT_DELETED_INTEGRATION = "integrations.deleted";
	private static readonly EVENT_SAVED_INTEGRATION = "integrations.saved";
	private static readonly EVENT_CHECK_INTEGRATION_CONFIGURATION = "integrations.check.configuration";
	private static readonly EVENT_INTEGRATION_CONFIGURATION_CHECKED = "integrations.configuration.checked";

	private addIntegrationDialogHandler: AddIntegrationDialogHandler;
	private getEnabledIntegrationTypesRequest: JQuery.jqXHR;
	private createIntegrationRequest: JQuery.jqXHR;
	private updateIntegrationRequest: JQuery.jqXHR;
	private deleteIntegrationRequest: JQuery.jqXHR;
	private checkIntegrationConfigurationRequest: JQuery.jqXHR<void>;

	private enabledIntegrations: Integration[];

	// tslint:disable-next-line:variable-name
	private static _instance: Integrations;

	private constructor() {
		this.getEnabledIntegrations();
		this.initAddIntegrationButton();
		this.initListeners();
	}

	public static get instance(): Integrations {
		if (!this._instance) {
			this._instance = new Integrations();
			this._instance.initIntegrations();
		}
		return this._instance;
	}

	public initIntegrations() {
		// DEVNOTE: Here you can add your own integration
		// Integrations.onAddIntegration(IntegrationType.TIMR, (e, integrationType) => timrConfigDialog.show());
		Integrations.onAddIntegration(IntegrationType.PERSONIO, () => PersonioConfigDialog.create());
		Integrations.onAddIntegration(IntegrationType.SLACK, (e, integrationType) => Integrations.triggerAddedIntegrationEvent(integrationType, "{}"));
		Integrations.onAddIntegration(IntegrationType.ADDISON, () => AddisonEAUConfigDialog.create());
		Integrations.onAddIntegration(IntegrationType.ADDISON_SYNC, (e, integrationType) => Integrations.triggerAddedIntegrationEvent(integrationType, "{}"));
		Integrations.onAddIntegration(IntegrationType.JIRA, (e, integrationType) => Integrations.triggerAddedIntegrationEvent(integrationType, "{}"));
		Integrations.onAddIntegration(IntegrationType.CUSTOM, (e, integrationType) => Integrations.triggerAddedIntegrationEvent(integrationType, "{}"));
		Integrations.onAddIntegration(IntegrationType.BMD, () => BmdConfigDialog.create());
		Integrations.onAddIntegration(IntegrationType.GOOGLE, (e, integrationType) => Integrations.triggerAddedIntegrationEvent(integrationType, "{}"));
		Integrations.onAddIntegration(IntegrationType.MICROSOFT, (e, integrationType) => Integrations.triggerAddedIntegrationEvent(integrationType, "{}"));
	}

	/* Global event trigger */

	/*
	 * This event should be triggered when the user wants to add an integration
	 */
	public static triggerAddIntegrationEvent(integrationType: IntegrationType) {
		$(this.instance).triggerHandler(Integrations.EVENT_ADD_INTEGRATION, integrationType);
		$(this.instance).triggerHandler(`${Integrations.EVENT_ADD_INTEGRATION}-${integrationType}`, integrationType);
	}

	/*
	 * This event should be triggered when the integration should be saved to the database
	 */
	public static triggerAddedIntegrationEvent(integrationType: IntegrationType, json: string, args?: unknown[]) {
		$(this.instance).triggerHandler(Integrations.EVENT_ADDED_INTEGRATION, [integrationType, json, args]);
		$(this.instance).triggerHandler(`${Integrations.EVENT_ADDED_INTEGRATION}-${integrationType}`, [integrationType, json, args]);
	}

	/*
	 * This event should be triggered when the integration should be edited in the database
	 */
	public static triggerEditedIntegrationEvent(integrationType: IntegrationType, integration: Integration, json: string, args?: unknown[]) {
		$(this.instance).triggerHandler(Integrations.EVENT_EDITED_INTEGRATION, [integration, json, args]);
		$(this.instance).triggerHandler(`${Integrations.EVENT_EDITED_INTEGRATION}-${integrationType}`, [integration, json, args]);
	}

	/*
	 * This event should be triggered if the integration should be deleted from the database
	 */
	public static triggerDeletedIntegrationEvent(integrationType: IntegrationType, integrationId: number) {
		$(this.instance).triggerHandler(Integrations.EVENT_DELETED_INTEGRATION, integrationId);
		$(this.instance).triggerHandler(`${Integrations.EVENT_DELETED_INTEGRATION}-${integrationType}`, integrationId);
	}

	/*
	 * This event should be triggered if the configuration should be checked
	 */
	public static triggerCheckIntegrationConfigurationEvent(integrationType: IntegrationType, json: string) {
		$(this.instance).triggerHandler(Integrations.EVENT_CHECK_INTEGRATION_CONFIGURATION, [integrationType, json]);
		$(this.instance).triggerHandler(`${Integrations.EVENT_CHECK_INTEGRATION_CONFIGURATION}-${integrationType}`, [integrationType, json]);
	}

	/*
	 * This event should be triggered after the integration is saved to the database
	 */
	private static triggerIntegrationSavedEvent(integrationType: IntegrationType, fieldErrors: FieldError[]) {
		$(this.instance).triggerHandler(Integrations.EVENT_SAVED_INTEGRATION, [fieldErrors]);
		$(this.instance).triggerHandler(`${Integrations.EVENT_SAVED_INTEGRATION}-${integrationType}`, [fieldErrors]);
	}

	/*
	 * This event should be triggered if the configuration was checked
	 */
	private static triggerIntegrationConfigurationCheckedEvent(integrationType: IntegrationType, success: boolean) {
		$(this.instance).triggerHandler(Integrations.EVENT_INTEGRATION_CONFIGURATION_CHECKED, [success]);
		$(this.instance).triggerHandler(`${Integrations.EVENT_INTEGRATION_CONFIGURATION_CHECKED}-${integrationType}`, [success]);
	}

	/* Global event listener */

	/*
	 * Triggers when the user wants to add an integration
	 * @param integrationType The type of the integration
	 * @param callback The callback function (should call Integrations.triggerAddedIntegrationEvent)
	 */
	public static onAddIntegration(integrationType: IntegrationType, callback: (e: JQuery.TriggeredEvent, integrationType: IntegrationType) => void) {
		$(this.instance).on(`${Integrations.EVENT_ADD_INTEGRATION}-${integrationType}`, callback);
	}

	/*
	 * Triggers when the validation of the configuration fails
	 * @param integrationType The type of the integration
	 */
	public static onSaveIntegration(integrationType: IntegrationType, callback: (e: JQuery.TriggeredEvent, fieldErrors: FieldError[]) => void) {
		$(this.instance).on(`${Integrations.EVENT_SAVED_INTEGRATION}-${integrationType}`, callback);
	}

	/*
	 * Triggers when the configuration of the integration was checked
	 * Returns a success flag if the credentials are valid
	 * @param integrationType The type of the integration
	 */
	public static onConfigurationChecked(integrationType: IntegrationType, callback: (e: JQuery.TriggeredEvent, success: boolean) => void) {
		$(this.instance).on(`${Integrations.EVENT_INTEGRATION_CONFIGURATION_CHECKED}-${integrationType}`, callback);
	}

	/* Integration specific event listener */

	private initAddIntegrationButton() {
		this.addIntegrationDialogHandler = new AddIntegrationDialogHandler();
		$("#addIntegrationButton").click(() => {
			this.addIntegrationDialogHandler.show(this.enabledIntegrations);
		});
	}

	private initListeners() {
		$(this).on(Integrations.EVENT_ADDED_INTEGRATION, async (e, integrationType: IntegrationType, json: string) => {
			const integration = await this.saveIntegration(integrationType, json);
			this.enabledIntegrations.push(integration);
			this.placeIntegrationBanner(integration, true);
		});
		$(this).on(Integrations.EVENT_EDITED_INTEGRATION, async (e, integration: Integration, json: string) => {
			const i = await this.editIntegration(integration, json);
			const banner = document.getElementById("banner-" + integration.id) as PanelBase;
			banner.integration = i;
		});
		$(this).on(Integrations.EVENT_DELETED_INTEGRATION, (e, id: number) => {
			this.deleteIntegration(id);
		});
		$(this).on(Integrations.EVENT_CHECK_INTEGRATION_CONFIGURATION, async (e, integrationType: IntegrationType, json: string) => {
			await this.checkIntegrationConfiguration(integrationType, json);
		});
	}

	/* DOM manipulation - Banners */

	/*
	 * Places a banner for the given integration
	 * banner-${integration.type} is the name of the banner component
	 */
	private placeIntegrationBanner(integration: Integration, added: boolean = false) {
		const banner = document.createElement(`banner-${integration.type}`) as PanelBase;
		banner.id = "banner-" + integration.id;
		banner.integration = integration;
		if (added) {
			banner.onIntegrationAdded();
		}
		$("#enabledIntegrations").append(banner);
	}

	/*
	 * Removes the banner for the given integration
	 */
	private removeIntegrationBanner(id: number) {
		$("#enabledIntegrations").find(`#banner-${id}`).remove();
	}

	/* Requests */

	private getEnabledIntegrations() {
		this.getEnabledIntegrationTypesRequest?.abort();
		this.getEnabledIntegrationTypesRequest = api("integrations/getEnabledIntegrations.json")
			.get()
			.done((data: Integration[]) => {
				this.enabledIntegrations = data;
				this.enabledIntegrations.forEach(i => this.placeIntegrationBanner(i));
			});
	}

	private async saveIntegration(integrationType: IntegrationType, json?: string): Promise<Integration> {
		const deferred = $.Deferred();
		const params = [];
		params.push({name: "type", value: integrationType});
		params.push({name: "configuration", value: json});

		this.createIntegrationRequest?.abort();
		this.createIntegrationRequest = api("integrations/createIntegration.json")
			.post(params)
			.done((data: Integration) => {
				void deferred.resolve(data);
				Integrations.triggerIntegrationSavedEvent(integrationType, null);
			})
			.fail((response: ErrorResponse) => {
				if (response.responseJSON?.fieldErrors) {
					Integrations.triggerIntegrationSavedEvent(integrationType, response.responseJSON.fieldErrors);
				}
			});

		return deferred.promise() as JQueryPromise<Integration>;
	}

	private async editIntegration(integration: Integration, json?: string): Promise<Integration> {
		const deferred = $.Deferred();
		const params = [];
		params.push({name: "id", value: integration.id});
		params.push({name: "configuration", value: json});

		this.updateIntegrationRequest?.abort();
		this.updateIntegrationRequest = api("integrations/updateIntegration.json")
			.post(params)
			.done((data: Integration) => {
				void deferred.resolve(data);
				Integrations.triggerIntegrationSavedEvent(integration.type, null);
			})
			.fail((response: ErrorResponse) => {
				if (response.responseJSON?.fieldErrors) {
					Integrations.triggerIntegrationSavedEvent(integration.type, response.responseJSON.fieldErrors);
				}
			});

		return deferred.promise() as JQueryPromise<Integration>;
	}

	private deleteIntegration(id: number) {
		this.deleteIntegrationRequest?.abort();
		this.deleteIntegrationRequest = api("integrations/deleteIntegration.json")
			.post({id: id})
			.done(() => {
				const index = this.enabledIntegrations.map(i => i.id).indexOf(id, 0);
				if (index > -1) {
					this.enabledIntegrations.splice(index, 1);
				}
				this.removeIntegrationBanner(id);
			});
	}

	private async checkIntegrationConfiguration(integrationType: IntegrationType, json?: string): Promise<JQuery.jqXHR<void>> {
		const params = [];
		params.push({name: "type", value: integrationType});
		params.push({name: "configuration", value: json});

		this.checkIntegrationConfigurationRequest?.abort();
		this.checkIntegrationConfigurationRequest = api("integrations/checkIntegrationConfiguration.json")
			.post(params)
			.done(() => {
				Integrations.triggerIntegrationConfigurationCheckedEvent(integrationType, true);
			})
			.fail(() => {
				Integrations.triggerIntegrationConfigurationCheckedEvent(integrationType, false);
			});

		return this.checkIntegrationConfigurationRequest;
	}
}
